import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import ApplicantHeaderChip from '../primitives/ApplicantHeaderChip';
import PillGroup from '../primitives/PillGroup';
import DateTriplet from '../primitives/DateTriplet';
import TextField from '../primitives/TextField';
import SelectField from '../primitives/SelectField';
import CurrencyField from '../primitives/CurrencyField';
import AddressAutocomplete from '../primitives/AddressAutocomplete';
import RepeaterCard from '../primitives/RepeaterCard';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';
import { coverageAnalysis, formatCoverage } from '../dateUtils';
import { applicantDisplayName } from '../steps.config';
import { COUNTRY_OPTIONS, countryName } from '../countries';
import { isAUAddress, combinedStreet, addressSearchHint } from '../addressRules';
import GapAlert from '../primitives/GapAlert';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const ADDRESS_TYPE_OPTIONS = ['Renting', 'Owned', 'Mortgaged', 'Living with parents', 'Boarding', 'Other'];
const STATE_OPTIONS = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];


class StepAddressHistory extends React.Component {
  applicant = () => this.props.applicants.find((a) => a.id === this.props.applicantId) || this.props.applicants[0];

  patchCurrent = (field, value) => {
    const applicant = this.applicant();
    const next = { ...(applicant.address || {}), current: { ...((applicant.address || {}).current || {}), [field]: value } };
    this.props.onUpdateApplicant(applicant.id, { address: next });
  };

  // Read the address block a slot currently holds.
  readAddress = (slot, index) => {
    const address = this.applicant().address || {};
    return slot === 'current'
      ? address.current || {}
      : (address.previous || [])[index] || {};
  };

  // Write a whole address block back in one update. Everything that changes
  // more than one field at a time must go through here: patching twice in the
  // same handler reads stale props on the second call and silently reverts the
  // first change.
  writeAddress = (slot, index, nextAddr) => {
    const applicant = this.applicant();
    const address = applicant.address || {};
    if (slot === 'current') {
      this.props.onUpdateApplicant(applicant.id, {
        address: { ...address, current: nextAddr },
      });
    } else {
      const prev = [...(address.previous || [])];
      prev[index] = nextAddr;
      this.props.onUpdateApplicant(applicant.id, {
        address: { ...address, previous: prev },
      });
    }
  };

  applyAutocomplete = (slot, indexOrNull, selection) => {
    const base = this.readAddress(slot, indexOrNull);
    // If Google gives us a country back, sync it onto the address so the
    // selector and the State field stay in step.
    const country = (selection.country || base.country || 'au').toLowerCase();
    const next = {
      ...base,
      address_search: selection.formatted,
      country,
      unit_number: selection.unit_number || '',
      street_number: selection.street_number || '',
      street_name: selection.street_name || '',
      suburb: selection.suburb || '',
      state: selection.state || '',
      postcode: selection.postcode || '',
    };
    // Keep the combined `street` in sync as a back-compat convenience.
    next.street = combinedStreet(next);
    this.writeAddress(slot, indexOrNull, next);
  };

  patchAddressPiece = (slot, index, field, value) => {
    // Update the individual piece and recompute the combined `street` in the
    // same write.
    const next = { ...this.readAddress(slot, index), [field]: value };
    next.street = combinedStreet(next);
    this.writeAddress(slot, index, next);
  };

  patchPrevious = (index, field, value) => {
    const applicant = this.applicant();
    const prev = [...((applicant.address || {}).previous || [])];
    prev[index] = { ...(prev[index] || {}), [field]: value };
    this.props.onUpdateApplicant(applicant.id, {
      address: { ...(applicant.address || {}), previous: prev },
    });
  };

  addPrevious = () => {
    const applicant = this.applicant();
    const prev = [...((applicant.address || {}).previous || [])];
    // Default new previous addresses to whatever country their current
    // address uses - most people moved within the same country.
    const seedCountry =
      ((applicant.address || {}).current || {}).country || 'au';
    prev.push({
      address_search: '',
      country: seedCountry,
      unit_number: '',
      street_number: '',
      street_name: '',
      street: '', suburb: '', state: '', postcode: '',
      address_type: '', address_amount: '', other_text: '',
      moved_in: { day: '', month: '', year: '' },
      moved_out: { day: '', month: '', year: '' },
    });
    this.props.onUpdateApplicant(applicant.id, {
      address: { ...(applicant.address || {}), previous: prev },
    });
  };

  removePrevious = (index) => {
    const applicant = this.applicant();
    const prev = [...((applicant.address || {}).previous || [])];
    prev.splice(index, 1);
    this.props.onUpdateApplicant(applicant.id, {
      address: { ...(applicant.address || {}), previous: prev },
    });
  };

  copyFromApplicant = (sourceId) => {
    const { applicants, onUpdateApplicant } = this.props;
    const source = applicants.find((a) => a.id === sourceId);
    if (!source || !source.address) return;
    const target = this.applicant();
    onUpdateApplicant(target.id, {
      address: deepClone(source.address),
    });
  };

  computeCoverage = (current, previous) => {
    const entries = [];
    if (current && current.moved_in) {
      entries.push({ startDate: current.moved_in, endDate: null });
    }
    (previous || []).forEach((p) => {
      if (p.moved_in) {
        entries.push({ startDate: p.moved_in, endDate: p.moved_out });
      }
    });
    return coverageAnalysis(entries);
  };

  renderAddressFields = (slot, index, addr) => {
    const isRentLike = ['Renting', 'Boarding'].includes(addr.address_type);
    const isOther = addr.address_type === 'Other';
    const prefix = slot === 'current' ? 'address.current' : `address.previous.${index}`;
    const errs = this.props.errors || {};
    const country = (addr.country || 'au').toLowerCase();
    const isAU = isAUAddress(country);

    return (
      <>
        <SelectField
          label="Country"
          required
          options={COUNTRY_OPTIONS}
          value={country}
          onChange={(v) => {
            if (slot === 'current') this.patchCurrent('country', v);
            else this.patchPrevious(index, 'country', v);
          }}
          fieldId={`${prefix}.country`}
          error={errs[`${prefix}.country`]}
          hint={`The autocomplete below will search ${countryName(country)} addresses.`}
        />

        <AddressAutocomplete
          label="Search address"
          required
          // The search box keeps its own free-text value so typing doesn't
          // splat into the parsed street/suburb/state/postcode fields.
          value={addr.address_search || ''}
          country={country}
          onChange={(v) => {
            if (slot === 'current') this.patchCurrent('address_search', v);
            else this.patchPrevious(index, 'address_search', v);
          }}
          onSelect={(sel) => this.applyAutocomplete(slot, index, sel)}
          fieldId={`${prefix}.address_search`}
          error={errs[`${prefix}.address_search`]}
          hint={addressSearchHint(country)}
        />

        {/* Overseas addresses skip the split street fields: street numbering,
            states and postcodes vary too much country to country, so we rely
            on the address the customer picks from Google Places. */}
        {isAU ? (
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_2fr] gap-3">
            <TextField
              label="Unit / apt"
              value={addr.unit_number}
              onChange={(v) => this.patchAddressPiece(slot, index, 'unit_number', v)}
              fieldId={`${prefix}.unit_number`}
            />
            <TextField
              label="Street #"
              required
              inputMode="numeric"
              value={addr.street_number}
              onChange={(v) => this.patchAddressPiece(slot, index, 'street_number', v)}
              fieldId={`${prefix}.street_number`}
              error={errs[`${prefix}.street_number`]}
            />
            <TextField
              label="Street name"
              required
              value={addr.street_name}
              onChange={(v) => this.patchAddressPiece(slot, index, 'street_name', v)}
              fieldId={`${prefix}.street_name`}
              error={errs[`${prefix}.street_name`]}
            />
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
          <TextField
            label={isAU ? 'Suburb' : 'City / suburb'}
            required
            value={addr.suburb}
            onChange={(v) => slot === 'current' ? this.patchCurrent('suburb', v) : this.patchPrevious(index, 'suburb', v)}
            fieldId={`${prefix}.suburb`}
            error={errs[`${prefix}.suburb`]}
          />
          {isAU ? (
            <SelectField
              label="State"
              required
              placeholder="Select"
              options={STATE_OPTIONS}
              value={addr.state}
              onChange={(v) => slot === 'current' ? this.patchCurrent('state', v) : this.patchPrevious(index, 'state', v)}
              fieldId={`${prefix}.state`}
              error={errs[`${prefix}.state`]}
            />
          ) : (
            // Not every country has states or provinces, so overseas entries
            // stay optional and take whatever Google Places returns.
            <TextField
              label="State / province (optional)"
              value={addr.state}
              onChange={(v) => slot === 'current' ? this.patchCurrent('state', v) : this.patchPrevious(index, 'state', v)}
              fieldId={`${prefix}.state`}
              error={errs[`${prefix}.state`]}
            />
          )}
          <TextField
            label={isAU ? 'Postcode' : 'Postal code (optional)'}
            required={isAU}
            inputMode={isAU ? 'numeric' : 'text'}
            maxLength={isAU ? 4 : 12}
            value={addr.postcode}
            onChange={(v) => {
              const cleaned = isAU ? v.replace(/\D/g, '') : v;
              if (slot === 'current') this.patchCurrent('postcode', cleaned);
              else this.patchPrevious(index, 'postcode', cleaned);
            }}
            fieldId={`${prefix}.postcode`}
            error={errs[`${prefix}.postcode`]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1F2937] mb-2">
            Living arrangement <span className="text-[#DC2626]">*</span>
          </label>
          <PillGroup
            columns={3}
            options={ADDRESS_TYPE_OPTIONS}
            value={addr.address_type}
            onChange={(v) => slot === 'current' ? this.patchCurrent('address_type', v) : this.patchPrevious(index, 'address_type', v)}
            fieldId={`${prefix}.address_type`}
            error={errs[`${prefix}.address_type`]}
          />
        </div>

        {isRentLike ? (
          <CurrencyField
            label="Rent / board (per month)"
            required
            value={addr.address_amount}
            onChange={(v) => slot === 'current' ? this.patchCurrent('address_amount', v) : this.patchPrevious(index, 'address_amount', v)}
            suffix="/month"
            fieldId={`${prefix}.address_amount`}
            error={errs[`${prefix}.address_amount`]}
          />
        ) : null}

        {isOther ? (
          <TextField
            label="Please describe"
            required
            value={addr.other_text}
            onChange={(v) => slot === 'current' ? this.patchCurrent('other_text', v) : this.patchPrevious(index, 'other_text', v)}
            fieldId={`${prefix}.other_text`}
            error={errs[`${prefix}.other_text`]}
          />
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DateTriplet
            label="Moved in"
            required
            value={addr.moved_in}
            onChange={(v) => slot === 'current' ? this.patchCurrent('moved_in', v) : this.patchPrevious(index, 'moved_in', v)}
            fieldId={`${prefix}.moved_in`}
            error={errs[`${prefix}.moved_in`]}
          />
          {slot === 'previous' ? (
            <DateTriplet
              label="Moved out"
              required
              value={addr.moved_out}
              onChange={(v) => this.patchPrevious(index, 'moved_out', v)}
              fieldId={`${prefix}.moved_out`}
              error={errs[`${prefix}.moved_out`]}
            />
          ) : null}
        </div>
      </>
    );
  };

  render() {
    const { applicants, applicantId } = this.props;
    const idx = applicants.findIndex((a) => a.id === applicantId);
    const applicantIndex = idx === -1 ? 0 : idx;
    const applicant = applicants[applicantIndex];
    const address = applicant.address || { current: {}, previous: [] };
    const current = address.current || {};
    const previous = address.previous || [];
    const analysis = this.computeCoverage(current, previous);
    const monthsCovered = analysis.months;
    const hasGap = analysis.hasGap;
    const meetsThreeYears = monthsCovered >= 36 && !hasGap;

    return (
      <div className="space-y-5">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About you - Address history"
            title="3 years of address history"
            description="Start with where you live now, then add previous addresses until you've covered 3 full years."
          />
          <ApplicantHeaderChip
            applicant={applicant}
            applicantIndex={applicantIndex}
            totalApplicants={applicants.length}
          />

          {applicants.length > 1 && applicantIndex > 0 ? (
            <div className="mb-6 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">
                Lives with another applicant?
              </p>
              <p className="text-xs text-[#6B7280] mb-3">
                Save time by copying their address history. You can still edit individual fields after.
              </p>
              <div className="flex flex-wrap gap-2">
                {applicants
                  .filter((other) => other.id !== applicant.id)
                  .map((other) => {
                    const otherIndex = applicants.findIndex((a) => a.id === other.id);
                    return (
                      <button
                        key={other.id}
                        type="button"
                        onClick={() => this.copyFromApplicant(other.id)}
                        className="inline-flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors"
                        style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 2a2 2 0 00-2 2v1H5a2 2 0 00-2 2v9a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H8zm0 2h4v1H8V4zM5 7h10v9H5V7z" />
                        </svg>
                        Copy from {applicantDisplayName(other, otherIndex)}
                      </button>
                    );
                  })}
              </div>
            </div>
          ) : null}

          <HelperNote tone={meetsThreeYears ? undefined : 'warning'}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span>
                <strong>{meetsThreeYears ? 'Nice work, 3 years covered.' : 'Keep going.'} </strong>
                <strong>{formatCoverage(monthsCovered)}</strong> of history so far. Minimum is 3 years.
              </span>
              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold"
                style={{
                  backgroundColor: meetsThreeYears ? '#DCFCE7' : '#FEF3C7',
                  color: meetsThreeYears ? '#166534' : '#92400E',
                }}
              >
                {formatCoverage(monthsCovered)} / 3y
              </span>
            </div>
          </HelperNote>

          {hasGap ? (
            <div className="mt-4">
              <GapAlert kind="address" gaps={analysis.gaps} />
            </div>
          ) : null}
        </div>

        {/* Current address */}
        <CategoryCard
          icon={ICONS.pin}
          title="Current address"
          description="Where you live right now."
        >
          <RepeaterCard title="Where you live now" removable={false} badge="Current">
            {this.renderAddressFields('current', null, current)}
          </RepeaterCard>
        </CategoryCard>

        {/* Previous addresses */}
        <CategoryCard
          icon={ICONS.history}
          title="Previous addresses"
          description="Add previous addresses to cover at least 3 years."
          count={previous.length}
        >
          {previous.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
              <p className="text-sm text-[#6B7280]">
                No previous addresses yet. Add one if your current address has been yours for less than 3 years.
              </p>
            </div>
          ) : null}

          {previous.map((addr, idx) => (
            <RepeaterCard
              key={idx}
              title={`Previous address ${idx + 1}`}
              onRemove={() => this.removePrevious(idx)}
            >
              {this.renderAddressFields('previous', idx, addr)}
            </RepeaterCard>
          ))}

          <button
            type="button"
            onClick={this.addPrevious}
            className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold transition-colors"
            style={{
              borderColor: '#7C5CFC',
              color: '#5B3FE0',
              backgroundColor: '#FFFFFF',
            }}
          >
            + Add previous address
          </button>

          {hasGap ? (
            <div className="mt-4">
              <GapAlert kind="address" gaps={analysis.gaps} />
            </div>
          ) : null}
        </CategoryCard>
      </div>
    );
  }
}

export default StepAddressHistory;
